// On-device store for the Hub. Nothing here leaves the browser unless the
// person explicitly uses an existing sync or share feature elsewhere in the app.

export type CheckIn = {
  date: string; // YYYY-MM-DD
  mood: 1 | 2 | 3 | 4 | 5;
  energy: 1 | 2 | 3 | 4 | 5;
  note?: string;
};

const KEYS = {
  checkins: "mls.hub.checkins.v1",
  saved: "mls.hub.saved.v1",
  read: "mls.hub.read.v1",
} as const;

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — stay silent, the feature is optional */
  }
}

export const todayKey = () => new Date().toISOString().slice(0, 10);

export const getCheckIns = () => read<CheckIn[]>(KEYS.checkins, []);

export function saveCheckIn(entry: CheckIn) {
  const rest = getCheckIns().filter((c) => c.date !== entry.date);
  const next = [...rest, entry].sort((a, b) => a.date.localeCompare(b.date)).slice(-180);
  write(KEYS.checkins, next);
  return next;
}

export const getSavedTopics = () => read<string[]>(KEYS.saved, []);

export function toggleSavedTopic(id: string) {
  const current = getSavedTopics();
  const next = current.includes(id) ? current.filter((t) => t !== id) : [...current, id];
  write(KEYS.saved, next);
  return next;
}

export const getReadTopics = () => read<string[]>(KEYS.read, []);

export function markTopicRead(id: string) {
  const current = getReadTopics();
  if (current.includes(id)) return current;
  const next = [...current, id];
  write(KEYS.read, next);
  return next;
}

/** Consecutive days ending today (or yesterday) with a check-in. */
export function streak(entries: CheckIn[]): number {
  const set = new Set(entries.map((e) => e.date));
  let count = 0;
  const d = new Date();
  if (!set.has(d.toISOString().slice(0, 10))) d.setDate(d.getDate() - 1);
  for (;;) {
    const key = d.toISOString().slice(0, 10);
    if (!set.has(key)) break;
    count += 1;
    d.setDate(d.getDate() - 1);
  }
  return count;
}
